'use client';

import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { EmptyState } from '../../molecules/EmptyState';
import { ProductBreadcrumbs } from '../../molecules/ProductBreadcrumbs';
import { ProductListSorting } from '../../molecules/ProductListSorting';
import {
  ProductListExistingActions,
  ProductListHeader,
  ProductListNewActions,
} from '../../organisms/ProductListHeader';
import { ProductListInfo } from '../../organisms/ProductListInfo';
import type { UserListTemplateProps } from './UserListTemplate.types';

export const UserListTemplate = (props: UserListTemplateProps) => {
  const {
    className = '',
    breadcrumbs,
    name,
    info,
    sorting,
    isEmpty,
    hideSortAndFilters,
    children,
    microcopy,
    onSortChange,
    onFiltersChange,
    type,
  } = props;

  const [selectedSort, setSelectedSort] = useState<string | null>(
    sorting.selectedSort
  );
  const [selectedFilters, setSelectedFilters] = useState<string[]>(
    sorting.selectedFilters
  );

  const handleSortChange = (value: string | null) => {
    setSelectedSort(value);
    onSortChange?.(value);
  };

  const handleFiltersChange = (values: string[]) => {
    setSelectedFilters(values);
    onFiltersChange?.(values);
  };

  const handleClear = () => {
    handleSortChange(null);
    handleFiltersChange([]);
  };

  const renderHeader = () => {
    if (type === 'new') {
      return (
        <>
          <ProductListHeader
            type="new"
            size="xl"
            name={name}
            microcopy={props.microcopy.header}
            favoriteListModalProps={props.favoriteListModalProps}
            className="hidden md:flex"
          />
          <ProductListHeader
            type="default"
            size="xl"
            name={name}
            className="flex md:hidden"
          />
        </>
      );
    }

    if (type === 'existingDefault') {
      return <ProductListHeader type="default" size="xl" name={name} />;
    }

    return (
      <>
        <ProductListHeader
          type="existing"
          size="xl"
          name={name}
          microcopy={props.microcopy.header}
          favoriteListModalProps={props.favoriteListModalProps}
          confirmationPopupProps={props.confirmationPopupProps}
          shareListModalProps={props.shareListModalProps}
          hideShare={props.hideShare}
          className="hidden md:flex"
        />
        <ProductListHeader
          type="default"
          size="xl"
          name={name}
          className="flex md:hidden"
        />
      </>
    );
  };

  const renderMobileActions = () => {
    if (props.type === 'new') {
      return (
        <ProductListNewActions
          microcopy={props.microcopy.header}
          favoriteListModalProps={props.favoriteListModalProps}
          className="mt-5 md:hidden"
        />
      );
    }

    if (props.type === 'existing') {
      return (
        <ProductListExistingActions
          microcopy={props.microcopy.header}
          favoriteListModalProps={props.favoriteListModalProps}
          confirmationPopupProps={props.confirmationPopupProps}
          shareListModalProps={props.shareListModalProps}
          hideShare={props.hideShare}
          className="mt-5 md:hidden"
        />
      );
    }

    return null;
  };

  return (
    <div
      className={c('lg-container min-h-[25rem] pb-10 pt-4 lg:pb-16', className)}
      data-test="user-list-template"
    >
      <ProductBreadcrumbs path={breadcrumbs} className="mb-10 lg:mb-12" />
      <div>
        {renderHeader()}
        <div className="mt-2 flex flex-col justify-between md:gap-4 lg:flex-row lg:items-end">
          <ProductListInfo {...info} microcopy={microcopy.info} />
          {renderMobileActions()}
          {!isEmpty && !hideSortAndFilters && (
            <ProductListSorting
              {...sorting}
              selectedSort={selectedSort}
              selectedFilters={selectedFilters}
              microcopy={microcopy.sorting}
              onSortChange={handleSortChange}
              onFiltersChange={handleFiltersChange}
              onClear={handleClear}
              className={c('md:mt-0', props.type === 'new' ? 'mt-3' : 'mt-5')}
            />
          )}
        </div>
      </div>
      <div className="mt-5 lg:mt-10">
        {isEmpty ? (
          <EmptyState
            microcopy={microcopy.emptyState}
            className="px-0 py-0 md:px-10 md:py-10"
            iconSizeMobile={177}
          />
        ) : (
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5 lg:gap-5"
            data-test="user-list-template-grid"
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

UserListTemplate.displayName = 'UserListTemplate';
